import * as React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { PROXY_STORE_CODE_KEY, PROXY_STORE_NAME_KEY } from '@/config'
import { storage } from '@/utils/storage'

const readStoreCode = (): string | null =>
  storage.get(PROXY_STORE_CODE_KEY) ?? null

const readStoreName = (): string | null =>
  storage.get(PROXY_STORE_NAME_KEY) ?? null

type ProxyStoreState = {
  storeCode: string | null
  storeName: string | null
  setProxyStore: (storeCode: string, storeName: string) => void
  clearProxyStore: () => void
}

const ProxyStoreContext = createContext<ProxyStoreState>({
  storeCode: null,
  storeName: null,
  setProxyStore: () => {},
  clearProxyStore: () => {},
})

export const ProxyStoreProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [storeCode, setStoreCode] = useState<string | null>(readStoreCode)
  const [storeName, setStoreName] = useState<string | null>(readStoreName)

  useEffect(() => {
    if (storeCode) {
      storage.set(PROXY_STORE_CODE_KEY, storeCode)
    } else {
      storage.remove(PROXY_STORE_CODE_KEY)
    }
  }, [storeCode])

  useEffect(() => {
    if (storeName) {
      storage.set(PROXY_STORE_NAME_KEY, storeName)
    } else {
      storage.remove(PROXY_STORE_NAME_KEY)
    }
  }, [storeName])

  const setProxyStore = useCallback((code: string, name: string) => {
    setStoreCode(code)
    setStoreName(name)
  }, [])

  const clearProxyStore = useCallback(() => {
    setStoreCode(null)
    setStoreName(null)
  }, [])

  const value = useMemo(
    () => ({ storeCode, storeName, setProxyStore, clearProxyStore }),
    [storeCode, storeName, setProxyStore, clearProxyStore]
  )

  return (
    <ProxyStoreContext.Provider value={value}>
      {children}
    </ProxyStoreContext.Provider>
  )
}

export const useProxyStore = () => useContext(ProxyStoreContext)
